/**
 * The Apollo error link: records GraphQL and network errors to RUM, filters
 * out non-actionable noise (aborts, offline, transient subscription churn),
 * and recovers from 401s by refreshing the session and retrying — or signing
 * the user out when the session is beyond recovery.
 *
 * Kept as a factory separate from ApolloClientProvider so the wiring is
 * testable with ApolloLink.execute against a stub terminating link.
 */
import { ApolloLink } from "@apollo/client"
import { ErrorLink } from "@apollo/client/link/error"
import { CombinedGraphQLErrors, ServerError } from "@apollo/client/errors"
import { EMPTY, catchError, from, switchMap, throwError } from "rxjs"

import { AwsRumClient } from "helpers/awsRumClient"
import { isConnectivityError } from "helpers/isConnectivityError"
import { describeNetworkError, unwrapUnconventionalError } from "helpers/networkErrorReporting"
import { refreshAccessToken, signOutExpiredSession } from "helpers/authSessionRecovery"

export interface RumConfig {
  rumAppMonitorId: string
  rumIdentityPool: string
  awsRegion: string
}

// Records an error to RUM enriched with the GraphQL operation name, current
// route and variables — shared by the GraphQL- and network-error branches of
// the error link below.
const recordRumError = (
  rumConfig: RumConfig,
  message: string,
  name: string,
  operation: { operationName?: string | null; variables?: unknown }
): void => {
  const rumClient = AwsRumClient(
    rumConfig.rumAppMonitorId,
    rumConfig.rumIdentityPool,
    rumConfig.awsRegion
  )
  const operationName = operation?.operationName ?? "unknown"
  const errorObj = new Error([message, `Operation: ${operationName}`].join(" | "))
  errorObj.name = name
  errorObj.stack = `Route: ${window.location.pathname} | Variables: ${JSON.stringify(operation.variables)}`
  rumClient?.recordError(errorObj)
}

// Classifies a network error and records it to RUM unless it is
// non-actionable noise: cancelled requests and client connectivity failures
// (which would also trigger a storm of failing cognito-identity fetches
// inside the RUM client while offline). Transient subscription connection
// churn never reaches this link — amplifySubscriptionLink retries it and only
// propagates errors it could not recover from, which deserve the record.
const reportNetworkError = (
  rumConfig: RumConfig,
  error: unknown,
  fallbackMessage: string,
  operation: ApolloLink.Operation
): void => {
  if ((error as { name?: string } | null | undefined)?.name === "AbortError") {
    return
  }

  // Unwrap Apollo's UnconventionalError so the checks below — and the RUM
  // record — see the real cause (Amplify's plain-object subscription errors)
  // instead of the opaque wrapper message.
  const cause = unwrapUnconventionalError(error)

  if (isConnectivityError(cause)) {
    console.log(`[Network error - offline, skipped RUM]: ${JSON.stringify(error)}`) // eslint-disable-line
    return
  }

  const { message, name } = describeNetworkError(cause, fallbackMessage)
  recordRumError(rumConfig, message, name, operation)
  const operationName = operation?.operationName ?? "unknown"
  console.log(`[Network error]: Operation: ${operationName} | ${JSON.stringify(error)}`) // eslint-disable-line
}

export const createErrorLink = (rumConfig: RumConfig): ErrorLink =>
  new ErrorLink(({ error, operation, forward }) => {
    const { skipRumLogging } = operation.getContext() || {}

    if (CombinedGraphQLErrors.is(error)) {
      // Handle GraphQL errors
      if (!skipRumLogging) {
        error.errors.forEach((err) => {
          recordRumError(rumConfig, err.message, "GraphQLError", operation)

          console.error(
            `[GraphQL error]: Message: ${err.message}, Location: ${JSON.stringify(
              err.locations
            )}, Path: ${err.path}`
          )
        })
      }
      return
    }

    // A 401 means the request carried an expired or missing access token.
    // Refresh the session once and retry the operation; when the session is
    // beyond recovery (dead refresh token in a long-open tab), sign out so
    // the login screen replaces the app instead of every poll repeating the
    // 401 forever.
    if (ServerError.is(error) && error.statusCode === 401) {
      return from(refreshAccessToken()).pipe(
        switchMap((outcome) => {
          if (outcome === "session-expired") {
            signOutExpiredSession()
            return EMPTY
          }
          // A transient refresh failure (Cognito unreachable, throttled, 5xx)
          // must not end a still-valid session — record the original 401 and
          // surface it to the caller; the next poll refreshes again.
          if (outcome === "refresh-failed") {
            reportNetworkError(rumConfig, error, error.message, operation)
            return throwError(() => error)
          }
          // ErrorLink subscribes this retry straight to the outer observer,
          // so its errors never re-enter the handler above — report them
          // here. A 401 that survives a successful refresh is real signal.
          return forward(operation).pipe(
            catchError((retryError: unknown) => {
              reportNetworkError(
                rumConfig,
                retryError,
                retryError instanceof Error ? retryError.message : String(retryError),
                operation
              )
              return throwError(() => retryError)
            })
          )
        })
      )
    }

    // Handle network errors (HTTP non-200, fetch failures, and Amplify
    // subscription errors Apollo wraps as UnconventionalError).
    reportNetworkError(rumConfig, error, error.message, operation)
  })
